import { getDocument, GlobalWorkerOptions, PDFDocumentProxy } from 'pdfjs-dist';
import JSZip from 'jszip';

// Set up PDF.js worker properly for Vite
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Debug function to help troubleshoot document processing issues
let DEBUG_DOCS = true;

function debugLog(...args: any[]) {
  if (DEBUG_DOCS) {
    console.log('[Document Debug]', ...args);
  }
}

// Progress callback type
export type ProgressCallback = (progress: number, message: string) => void;

// Configuration interface for document processing
export interface DocumentProcessingConfig {
  processAllPages: boolean;
  maxPages?: number; // Only used if processAllPages is false
  chunkSize: number;
  timeoutMs: number;
  enableIntelligentSummarization: boolean;
}

// Default configuration
export const DEFAULT_PROCESSING_CONFIG: DocumentProcessingConfig = {
  processAllPages: true,
  chunkSize: 10,
  timeoutMs: 60000,
  enableIntelligentSummarization: true
};

// Global functions for debugging
if (typeof window !== 'undefined') {
  (window as any).enableDocumentDebug = (enable: boolean) => {
    DEBUG_DOCS = enable;
    console.log('Document debugging', enable ? 'enabled' : 'disabled');
  };
  
  (window as any).testDocumentProcessor = async () => {
    console.log('Document processor test:');
    console.log('- PDF.js worker:', GlobalWorkerOptions.workerSrc);
    console.log('- JSZip available:', typeof JSZip !== 'undefined');
    console.log('- Supported formats: PDF, PPTX, DOCX');
    console.log('- Debug mode:', DEBUG_DOCS);
  };

  // Test PDF processing with a simple test
  (window as any).testPDFProcessing = async () => {
    console.log('Testing PDF processing setup...');
    try {
      // Create a simple test PDF-like buffer (this is just for testing the setup)
      const testBuffer = new ArrayBuffer(100);
      const uint8Array = new Uint8Array(testBuffer);
      // Set PDF header
      uint8Array[0] = 0x25; // %
      uint8Array[1] = 0x50; // P
      uint8Array[2] = 0x44; // D
      uint8Array[3] = 0x46; // F
      uint8Array[4] = 0x2D; // -
      
      console.log('PDF.js worker source:', GlobalWorkerOptions.workerSrc);
      console.log('Test completed - check console for any errors');
    } catch (error) {
      console.error('PDF processing test failed:', error);
    }
  };
}

// Extract text from PPTX files
async function extractTextFromPPTX(file: File): Promise<string> {
  debugLog('Processing PPTX file:', file.name);
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = new JSZip();
    const zipData = await zip.loadAsync(arrayBuffer);
    
    let extractedText = '';
    
    // Extract text from slide content
    const slideFiles = Object.keys(zipData.files).filter(filename => 
      filename.startsWith('ppt/slides/slide') && filename.endsWith('.xml')
    );
    
    debugLog('Found slide files:', slideFiles.length);
    
    for (const slideFile of slideFiles) {
      const slideContent = await zipData.files[slideFile].async('text');
      
      // Parse XML and extract text content
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(slideContent, 'text/xml');
      
      // Extract all text nodes from the slide
      const textElements = xmlDoc.querySelectorAll('a\\:t, t');
      textElements.forEach(element => {
        const text = element.textContent?.trim();
        if (text) {
          extractedText += text + ' ';
        }
      });
      
      extractedText += '\n\n';
    }
    
    // Also extract from notes if available
    const notesFiles = Object.keys(zipData.files).filter(filename => 
      filename.startsWith('ppt/notesSlides/') && filename.endsWith('.xml')
    );
    
    for (const notesFile of notesFiles) {
      const notesContent = await zipData.files[notesFile].async('text');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(notesContent, 'text/xml');
      
      const textElements = xmlDoc.querySelectorAll('a\\:t, t');
      textElements.forEach(element => {
        const text = element.textContent?.trim();
        if (text) {
          extractedText += text + ' ';
        }
      });
    }
    
    if (!extractedText.trim()) {
      throw new Error('No text content found in the PowerPoint file.');
    }
    
    debugLog('Extracted text length:', extractedText.length);
    return extractedText.trim();
    
  } catch (error) {
    console.error('Error processing PPTX:', error);
    throw new Error(`Failed to process PowerPoint file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Enhanced PDF extraction with progress feedback and better error handling
async function extractTextFromPDF(
  file: File, 
  progressCallback?: ProgressCallback,
  config: DocumentProcessingConfig = DEFAULT_PROCESSING_CONFIG
): Promise<string> {
  debugLog('Processing PDF file:', file.name);
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    debugLog('File size:', arrayBuffer.byteLength, 'bytes');
    
    // Verify it's a PDF file
    const uint8Array = new Uint8Array(arrayBuffer);
    const header = String.fromCharCode(...uint8Array.slice(0, 5));
    if (header !== '%PDF-') {
      throw new Error('Invalid PDF file format');
    }
    
    debugLog('Valid PDF header detected');
    progressCallback?.(10, 'PDF file validated, loading document...');
    
    // Load PDF with optimized settings for better performance
    const loadingTask = getDocument({
      data: arrayBuffer,
      useSystemFonts: false,
      verbosity: 0,
      useWorkerFetch: false,
      isEvalSupported: false,
      disableAutoFetch: true,
      disableStream: true,
      disableRange: true,
      stopAtErrors: false, // Changed to false to handle more PDFs
      maxImageSize: 1024 * 1024, // Increased image limit
      cMapUrl: '',
      cMapPacked: false
    });
    
    // Timeout promise with configurable timeout
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`PDF loading timeout after ${config.timeoutMs / 1000} seconds`)), config.timeoutMs);
    });
    
    const pdf = await Promise.race([loadingTask.promise, timeoutPromise]) as PDFDocumentProxy;
    debugLog('PDF loaded successfully, pages:', pdf.numPages);
    progressCallback?.(20, `PDF loaded successfully. Processing ${pdf.numPages} pages...`);
    
    let fullText = '';
    const totalPages = pdf.numPages;
    
    // Process pages based on configuration
    const chunkSize = config.chunkSize;
    const totalChunks = Math.ceil(totalPages / chunkSize);
    
    if (!config.processAllPages && config.maxPages) {
      const maxPages = Math.min(totalPages, config.maxPages);
      debugLog(`Processing first ${maxPages} pages out of ${totalPages} total pages (limited mode)`);
    } else {
      debugLog(`Processing all ${totalPages} pages in ${totalChunks} chunks of ${chunkSize} pages each`);
    }
    
    const maxPagesToProcess = config.processAllPages ? totalPages : (config.maxPages || 20);
    
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const startPage = chunkIndex * chunkSize + 1;
      const endPage = Math.min((chunkIndex + 1) * chunkSize, maxPagesToProcess);
      
      // Stop processing if we've reached the max pages limit
      if (startPage > maxPagesToProcess) {
        break;
      }
      
              progressCallback?.(
          20 + (chunkIndex / totalChunks) * 70, 
          `Processing pages ${startPage}-${endPage} of ${maxPagesToProcess}...`
        );
      
      // Process pages in current chunk
      for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          
          const pageText = textContent.items
            .map((item: any) => item.str)
            .join(' ');
          
          fullText += pageText + '\n\n';
          debugLog(`Page ${pageNum} processed, text length:`, pageText.length);
          
          // Yield to event loop to prevent blocking
          await new Promise(resolve => setTimeout(resolve, 0));
          
        } catch (pageError) {
          console.warn(`Failed to process page ${pageNum}:`, pageError);
          // Continue with other pages
        }
      }
      
      // Small delay between chunks to prevent overwhelming the system
      if (chunkIndex < totalChunks - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    progressCallback?.(95, 'Finalizing text extraction...');
    
    if (!fullText.trim()) {
      throw new Error('No text content found in the PDF file.');
    }
    
    debugLog('Total extracted text length:', fullText.length);
    progressCallback?.(100, 'PDF processing completed successfully');
    return fullText.trim();
    
  } catch (error) {
    console.error('Error processing PDF:', error);
    
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      
      if (errorMessage.includes('timeout')) {
        throw new Error(`PDF processing timed out after ${config.timeoutMs / 1000} seconds. The file may be very large or complex. Try with a smaller file or increase the timeout.`);
      } else if (errorMessage.includes('invalid pdf') || errorMessage.includes('not a pdf')) {
        throw new Error('Invalid PDF file. Please ensure the file is a valid PDF document.');
      } else if (errorMessage.includes('password')) {
        throw new Error('Password-protected PDFs are not supported. Please remove the password and try again.');
      } else if (errorMessage.includes('corrupted') || errorMessage.includes('damaged')) {
        throw new Error('The PDF file appears to be corrupted. Please try with a different file.');
      } else {
        throw new Error(`PDF processing error: ${error.message}`);
      }
    }
    
    throw new Error('Failed to process PDF file. Please check the console for more details.');
  }
}

// Extract text from DOCX files using basic ZIP parsing
async function extractTextFromDOCX(file: File): Promise<string> {
  debugLog('Processing DOCX file:', file.name);
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = new JSZip();
    const zipData = await zip.loadAsync(arrayBuffer);
    
    // Extract the main document content
    const documentXml = zipData.files['word/document.xml'];
    if (!documentXml) {
      throw new Error('Invalid DOCX file - missing document.xml');
    }
    
    const content = await documentXml.async('text');
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, 'text/xml');
    
    // Extract all text nodes
    const textElements = xmlDoc.querySelectorAll('w\\:t, t');
    let extractedText = '';
    
    textElements.forEach(element => {
      const text = element.textContent?.trim();
      if (text) {
        extractedText += text + ' ';
      }
    });
    
    if (!extractedText.trim()) {
      throw new Error('No text content found in the Word document.');
    }
    
    debugLog('Extracted text length:', extractedText.length);
    return extractedText.trim();
    
  } catch (error) {
    console.error('Error processing DOCX:', error);
    throw new Error(`Failed to process Word document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Main function to extract text from various document formats
export async function extractTextFromDocument(
  file: File, 
  progressCallback?: ProgressCallback,
  config: DocumentProcessingConfig = DEFAULT_PROCESSING_CONFIG
): Promise<string> {
  console.log('=== Starting Document Processing ===');
  debugLog('File:', file.name, 'Type:', file.type, 'Size:', file.size);
  
  // Set debug info for frontend
  if (typeof window !== 'undefined') {
    (window as any).documentProcessor = {
      type: 'multi-format',
      pdfWorker: GlobalWorkerOptions.workerSrc,
      jsZipAvailable: typeof JSZip !== 'undefined',
      supportedFormats: ['pdf', 'pptx', 'docx']
    };
  }
  
  const fileExtension = file.name.toLowerCase().split('.').pop();
  const mimeType = file.type.toLowerCase();
  
  debugLog('File extension:', fileExtension, 'MIME type:', mimeType);
  
  try {
    switch (fileExtension) {
      case 'pdf':
        if (mimeType !== 'application/pdf') {
          throw new Error('File has .pdf extension but incorrect MIME type');
        }
        return await extractTextFromPDF(file, progressCallback, config);
        
      case 'pptx':
        return await extractTextFromPPTX(file);
        
      case 'ppt':
        throw new Error('Legacy PPT files are not supported. Please convert to PPTX format.');
        
      case 'docx':
        return await extractTextFromDOCX(file);
        
      case 'doc':
        throw new Error('Legacy DOC files are not supported. Please convert to DOCX format.');
        
      default:
        throw new Error(`Unsupported file format: ${fileExtension}. Supported formats: PDF, PPTX, DOCX`);
    }
  } catch (error) {
    console.error('Document processing failed:', error);
    throw error;
  }
}

console.log('Document processor initialized with multi-format support');
debugLog('Supported formats: PDF, PPTX, DOCX');
debugLog('PDF processing mode: enhanced PDF.js with proper worker and incremental processing'); 